import {appQueries} from '@app/app-queries';
import {getSettingsPreviewMode} from '@common/admin/settings/preview/use-settings-preview-mode';
import {auth} from '@common/auth/use-auth';
import {getLocalizedUrl} from '@common/custom-page/use-localized-url';
import {queryClient} from '@common/http/query-client';
import {NotFoundPage} from '@common/ui/not-found-page/not-found-page';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {redirect, RouteObject} from 'react-router';

const configuredLocales: string[] = (
  getBootstrapData()?.i18n?.locales ?? []
).map(l => l.language);

// Landing-page route loader. Runs at /{locale} (each configured locale
// gets its own route) and decides:
//   logged in → bounce to /drive
//   guest + homepage.type==='loginPage' → /login
//   else → fetch landing data for the URL's locale and render
function landingLoader({params}: {params: {locale?: string}}) {
  const isLoggedIn = auth.isLoggedIn;
  const inPreview = getSettingsPreviewMode().isInsideSettingsPreview;
  if (
    !isLoggedIn &&
    getBootstrapData().settings.homepage.type === 'loginPage' &&
    !inPreview
  ) {
    return redirect('/login');
  }
  if (isLoggedIn && !inPreview) {
    return redirect(getBootstrapData().auth_redirect_uri);
  }
  return queryClient.ensureQueryData(
    appQueries.landingPageData.get(params.locale),
  );
}

// /{locale} and /{locale}/{slug} — enumerated as literal paths per
// configured locale rather than a generic `:locale` param so a non-locale
// segment (e.g. a legacy custom-page slug like /privacy-policy) falls
// through to the catch-all `:pageSlug` route instead of being swallowed by
// a single greedy match. Each locale gets the same two children: the
// landing page at the index, and the per-locale custom page at `:pageSlug`.
const localePrefixedRoutes: RouteObject[] = configuredLocales.map(locale => ({
  path: locale,
  children: [
    {
      index: true,
      loader: () => landingLoader({params: {locale}}),
      lazy: () => import('@app/landing-page'),
    },
    {
      path: ':pageSlug',
      lazy: () => import('@common/custom-page/custom-page-layout'),
    },
  ],
}));

export const commonRoutes: RouteObject[] = [
  // Legacy /contact links inside the SPA (e.g. older toast actions, deep
  // links from emails) — redirect to the locale-prefixed canonical so
  // client-side navigation matches what the backend 301 would do.
  {
    path: 'contact',
    loader: () => redirect(getLocalizedUrl('contact')),
  },
  // legacy /pages/<slug> URLs now redirect to the root-level slug
  {
    path: 'pages/:pageSlug',
    loader: ({params}) => redirect(`/${params.pageSlug}`),
  },
  {
    path: 'pages/:pageId/:pageSlug',
    loader: ({params}) => redirect(`/${params.pageSlug}`),
  },
  {
    path: '404',
    element: <NotFoundPage />,
  },
  ...localePrefixedRoutes,
  // Legacy /{slug} (no locale prefix) — client-side navigation redirects
  // to the locale-prefixed canonical so the address bar matches what the
  // backend would have served on a fresh load. We rewrite via the
  // bootstrap slug map (which knows each translated page's per-locale
  // slug) and fall back to /{active-locale}/{slug} for untranslated
  // pages. The backend's 301 handles direct hits the SPA never sees.
  {
    path: ':pageSlug',
    loader: ({params}) => {
      const data = getBootstrapData();
      const active = data?.i18n?.active || 'en';
      const slug = params.pageSlug ?? '';

      // built-in page (contact/pricing) URL typed without locale
      for (const builtin of Object.values(data?.builtinPages ?? {})) {
        if (
          builtin.slug === slug ||
          Object.values(builtin.translations ?? {}).some(s => s === slug)
        ) {
          const newSlug = builtin.translations?.[active] || builtin.slug;
          return redirect(`/${active}/${newSlug}`);
        }
      }

      // translated custom page whose slug we have in the map
      for (const [baseSlug, translations] of Object.entries(
        data?.customPageSlugs ?? {},
      )) {
        const matchedLocale = Object.entries(translations).find(
          ([, s]) => s === slug,
        )?.[0];
        if (baseSlug === slug || matchedLocale) {
          const newSlug = translations[active] || baseSlug;
          return redirect(`/${active}/${newSlug}`);
        }
      }

      // untranslated page — just prefix the active locale
      return redirect(`/${active}/${slug}`);
    },
  },
];
